import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Checkbox,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
  Text
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'

import { ActionButton } from '../../../components/ActionButton'
import { Icon } from '../../../components/Icon'
import { removeAccents } from '../../../helpers'
import { IRoleGroup } from '../../../services/UserGroup/UserGroupService'

interface IUserGroupPermissionsSelectorProps {
  permissions: IRoleGroup[]
  selected: number[]
  onChange: (permissions: number[]) => void
}

interface IPermissionGroupItemProps {
  group: IRoleGroup
  selectedIds: Set<number>
  depth: number
  /** Durante a busca as subseções já nascem abertas; fora dela, recolhidas. */
  isSearching: boolean
  onToggleRole: (roleId: number, checked: boolean) => void
  onToggleGroup: (group: IRoleGroup, checked: boolean) => void
}

/** IDs de todas as permissões do grupo e dos seus descendentes. */
function collectRoleIds(group: IRoleGroup): number[] {
  const own = group.roles?.map((role) => Number(role.id)) ?? []
  const fromChildren = group.children?.flatMap(collectRoleIds) ?? []

  return [...own, ...fromChildren]
}

function normalize(text: string): string {
  return removeAccents(text).toLowerCase()
}

function matches(text: string, term: string): boolean {
  return normalize(text).includes(term)
}

/**
 * Poda a árvore, mantendo só o que casa com a busca.
 *
 * Grupo cujo nome casa entra inteiro — buscar "eventos" traz a seção Eventos com os módulos todos.
 * Como o resultado é uma árvore normal, o resto do componente não sabe que houve filtro: o contador
 * e o "marcar tudo" passam a agir só sobre o que sobrou, que é o comportamento desejado.
 */
function filterTree(groups: IRoleGroup[], term: string): IRoleGroup[] {
  return groups.reduce<IRoleGroup[]>((kept, group) => {
    if (matches(group.name, term)) {
      kept.push(group)

      return kept
    }

    const roles = group.roles?.filter((role) => matches(role.name, term)) ?? []
    const children = filterTree(group.children ?? [], term)

    if (roles.length > 0 || children.length > 0) {
      kept.push({ ...group, roles, children })
    }

    return kept
  }, [])
}

function countGroups(groups: IRoleGroup[]): number {
  return groups.reduce((total, group) => total + 1 + countGroups(group.children ?? []), 0)
}

function PermissionGroupItem({
  group,
  selectedIds,
  depth,
  isSearching,
  onToggleRole,
  onToggleGroup
}: IPermissionGroupItemProps) {
  const roleIds = useMemo(() => collectRoleIds(group), [group])
  const checkedCount = roleIds.filter((id) => selectedIds.has(id)).length
  const allChecked = roleIds.length > 0 && checkedCount === roleIds.length
  const someChecked = checkedCount > 0 && !allChecked
  const isTopLevel = depth === 0

  return (
    <AccordionItem border="none" mb={1}>
      <Flex
        align="center"
        gap={2}
        pl={2}
        borderBottom="1px solid"
        borderColor="gray.100"
        borderRadius="md"
        _hover={{ bg: 'gray.50' }}
      >
        <Checkbox
          isChecked={allChecked}
          isIndeterminate={someChecked}
          isDisabled={roleIds.length === 0}
          onChange={(event) => onToggleGroup(group, event.target.checked)}
          aria-label={`Selecionar todas as permissões de ${group.name}`}
        />
        <AccordionButton flex="1" px={2} py={isTopLevel ? 2.5 : 2} _hover={{ bg: 'transparent' }}>
          <Box
            flex="1"
            textAlign="left"
            fontSize={isTopLevel ? 'md' : 'sm'}
            fontWeight={isTopLevel ? 'semibold' : 'medium'}
            color={isTopLevel ? 'darkGray.500' : 'magenta.500'}
          >
            {group.name}
          </Box>
          <Badge
            colorScheme={checkedCount > 0 ? 'magenta' : 'gray'}
            variant="subtle"
            borderRadius="full"
            fontSize="xs"
            px={2}
          >
            {checkedCount}/{roleIds.length}
          </Badge>
          <AccordionIcon ml={2} color="gray.500" />
        </AccordionButton>
      </Flex>

      <AccordionPanel pt={3} pb={3} pl={4} ml={4} borderLeft="2px solid" borderColor="gray.100">
        {group.roles?.length > 0 && (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacingX={4} spacingY={2}>
            {group.roles.map((role) => (
              <Checkbox
                key={role.id}
                isChecked={selectedIds.has(Number(role.id))}
                onChange={(event) => onToggleRole(Number(role.id), event.target.checked)}
              >
                {role.name}
              </Checkbox>
            ))}
          </SimpleGrid>
        )}

        {group.children?.length > 0 && (
          <Accordion
            allowMultiple
            mt={group.roles?.length > 0 ? 4 : 0}
            defaultIndex={isSearching ? group.children.map((_, index) => index) : []}
          >
            {group.children.map((child) => (
              <PermissionGroupItem
                key={child.id}
                group={child}
                selectedIds={selectedIds}
                depth={depth + 1}
                isSearching={isSearching}
                onToggleRole={onToggleRole}
                onToggleGroup={onToggleGroup}
              />
            ))}
          </Accordion>
        )}
      </AccordionPanel>
    </AccordionItem>
  )
}

/**
 * Árvore de permissões em accordion, com busca. Nasce toda recolhida: são ~170 permissões e a lista
 * plana empurrava o rodapé do modal para fora da tela.
 */
export function UserGroupPermissionsSelector({ permissions, selected, onChange }: IUserGroupPermissionsSelectorProps) {
  const [query, setQuery] = useState('')

  // Normaliza a prop `selected` para número (por segurança, caso venha com IDs como string).
  const selectedIds = useMemo(() => new Set(selected.map(Number)), [selected])

  const term = useMemo(() => normalize(query.trim()), [query])
  const visible = useMemo(() => (term ? filterTree(permissions, term) : permissions), [permissions, term])

  const applyChange = (ids: number[], checked: boolean) => {
    const next = new Set(selectedIds)
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)))

    onChange(Array.from(next))
  }

  const handleToggleRole = (roleId: number, checked: boolean) => applyChange([roleId], checked)

  const handleToggleGroup = (group: IRoleGroup, checked: boolean) => applyChange(collectRoleIds(group), checked)

  if (permissions.length === 0) {
    return <Text variant="caption">Nenhuma permissão disponível</Text>
  }

  const foundRoles = visible.flatMap(collectRoleIds).length

  return (
    <>
      <InputGroup size="sm" mb={3}>
        <InputLeftElement pointerEvents="none" color="gray.400">
          <Icon name="lni lni-search-alt" />
        </InputLeftElement>
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar permissão ou seção"
          aria-label="Buscar permissão"
          borderRadius="md"
        />
        {query && (
          <InputRightElement>
            <ActionButton action="cancel" label="Limpar busca" size="xs" mr={0} onClick={() => setQuery('')} />
          </InputRightElement>
        )}
      </InputGroup>

      {term && visible.length === 0 && (
        <Text variant="caption">Nenhuma permissão encontrada para &ldquo;{query.trim()}&rdquo;</Text>
      )}

      {term && visible.length > 0 && (
        <Text variant="caption" mb={2}>
          {foundRoles} {foundRoles === 1 ? 'permissão' : 'permissões'} em {countGroups(visible)}{' '}
          {countGroups(visible) === 1 ? 'seção' : 'seções'}
        </Text>
      )}

      {/*
        Remontar a cada termo é o que mantém as seções abertas durante a busca: o índice do Accordion
        do Chakra é posicional, então controlá-lo embaralharia a expansão conforme o filtro muda de
        tamanho. O campo de busca fica fora daqui, então o foco sobrevive à remontagem.
      */}
      <Accordion allowMultiple key={term} defaultIndex={term ? visible.map((_, index) => index) : []}>
        {visible.map((group) => (
          <PermissionGroupItem
            key={group.id}
            group={group}
            selectedIds={selectedIds}
            depth={0}
            isSearching={Boolean(term)}
            onToggleRole={handleToggleRole}
            onToggleGroup={handleToggleGroup}
          />
        ))}
      </Accordion>
    </>
  )
}
